import React, { useEffect } from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock, CodeEditor, BoldTextParagraph,   InlineCode,   SectionTitle,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider,  MutedText, Separator, CorrectionBox, Result} from '../../../../components/course/CourseComponents';

const Chapitre9Content = ({ courseId = 'baseprogrammation' }) => {
  
  // Effet pour donner le focus à l'éditeur quand le composant se monte
  useEffect(() => {
    // Le focus sera géré par la prop autoFocus du CodeEditor
  }, []);

  return (
    <>
     <InfoBox title="Objectif" color="blue">
        <p className="mb-0">
        Résumé de tout ce que tu as appris dans le cours de base de programmation.
        </p>
      </InfoBox>       

      <Separator type="text" text="Félicitations !" color="gray-darker" size="md" />
      <SectionTitle>🎉 Bravo ! Tu connais maintenant les bases de la programmation</SectionTitle>
      <p>
        Voici un petit résumé de tout ce que tu as appris dans ce cours JavaScript :
      </p>


      <Note type="gray" title="1️⃣ Afficher un message dans la console :">
        <CodeBlock language="javascript">
{`print("Bonjour");`}
        </CodeBlock>
      </Note>

      <Note type="gray" title="2️⃣ Créer une variable et l'utiliser :">
        <CodeBlock language="javascript">
{`let nom = "Michael";
print(nom);`}
        </CodeBlock>
      </Note>

      <Note type="gray" title="3️⃣ Faire des conditions :">
        <CodeBlock language="javascript">
{`if (age >= 18) {
  print("majeur");
} else {
  print("mineur");
}`}
        </CodeBlock>
      </Note>

      <Note type="gray" title="4️⃣ Faire des boucles :">
        <CodeBlock language="javascript">
{`for (let i = 0; i < 5; i++) {
  print(i);
}`}
        </CodeBlock>
      </Note>

      <Note type="gray" title="5️⃣ Utiliser des listes :">
        <CodeBlock language="javascript">
{`let fruits = ["pomme", "banane"];
print(fruits[0]);`}
        </CodeBlock>
      </Note>

      <Note type="gray" title="6️⃣ Créer des tableaux à 2 dimensions :">
        <CodeBlock language="javascript">
{`let grille = [[1, 2], [3, 4]];
print(grille[1][0]);`}
        </CodeBlock>
      </Note>

      <Note type="gray" title="7️⃣ Créer des structures (objets) :">
        <CodeBlock language="javascript">
{`let joueur = { nom: "Leo", score: 100 };
print(joueur.score);`}
        </CodeBlock>
      </Note>

      <Note type="gray" title="8️⃣ Créer des fonctions :">
        <CodeBlock language="javascript">
{`function bonjour(nom) {
  print("Bonjour " + nom);
}
bonjour("Michael");`}
        </CodeBlock>
      </Note>

      <Separator type="text" text="Exercice final" color="gray-darker" size="md" />
      <SectionTitle>Mini-projet final : Combat entre héros et ennemis</SectionTitle>
      
      <p>
        Pour terminer ce cours, on va créer un petit jeu en mode console qui utilise tout ce que tu as appris :
      </p>

      <Note type="info" title="🎯 Objectif du projet :">
        <p>Le but du jeu est simple : Vaincre tous les ennemis un par un grâce à la force du héros.</p>
      </Note>  
      <Note type="info" title="⚔️ Déroulement :">
        <ul className="list-disc list-inside space-y-1">
          <li>Le héros, Alex, affronte 3 ennemis (Gobelin, Orc, Dragon)</li>
          <li>À chaque tour, il inflige des dégâts aléatoires</li>
          <li>Le combat continue jusqu'à ce que chaque ennemi ait 0 PV</li>
          <li>Quand tous les ennemis sont battus, le jeu affiche "Tu as gagné !" 🏆</li>
        </ul>
      </Note>

      <Note type="exercice" title="🎮 À toi de jouer !">
        Essaie de créer ce programme étape par étape :<br/>
        - Crée un objet <InlineCode>hero</InlineCode> avec un nom et une force<br/>
        - Crée un tableau <InlineCode>ennemis</InlineCode> avec plusieurs ennemis, chacun ayant un nom et des PV<br/> 
        - Crée une fonction <InlineCode>attaquer</InlineCode> qui calcule les dégâts et les retire à l'ennemi <br/>
        - Ajoute un <InlineCode>print</InlineCode> pour annoncer le début de l'aventure <br/>
        - Fais une boucle <InlineCode>for</InlineCode> pour affronter chaque ennemi un par un <br/>
        - Dans la boucle, utilise <InlineCode>while</InlineCode> pour attaquer jusqu'à ce que l'ennemi ait 0 PV <br/>
        - Affiche un message quand l'ennemi est vaincu <br/>
        - Une fois tous les ennemis vaincus, affiche un message de victoire <br/>
        <span className="text-orange-600 dark:text-orange-400 font-medium">Le copier-coller est autorisé pour ce projet final !</span>
        <CorrectionBox>
          <CodeBlock language="javascript">
{`// Héros
let hero = {
  nom: "Alex",
  force: 10
};

// Liste d'ennemis
let ennemis = [
  { nom: "Gobelin", pv: 30 },
  { nom: "Orc", pv: 40 },
  { nom: "Dragon", pv: 60 }
];

// Fonction d'attaque
function attaquer(ennemi) {
  // dégâts = force du héros (10) + un nombre entier entre 0 et 5 (aléatoire)
  let degats = hero.force + Math.floor(Math.random() * 6); 
  print(hero.nom + " attaque " + ennemi.nom + " pour " + degats + " dégâts.");
  ennemi.pv -= degats;
  if (ennemi.pv < 0) ennemi.pv = 0;
  print(ennemi.nom + " a " + ennemi.pv + " PV restants.");
}

// Simulation
print("=== Début de l'aventure de " + hero.nom + " ===");

for (let i = 0; i < ennemis.length; i++) {
  let ennemi = ennemis[i];
  print("Un " + ennemi.nom + " apparaît avec " + ennemi.pv + " PV !");

  while (ennemi.pv > 0) {
    attaquer(ennemi);
  }

  print(ennemi.nom + " est vaincu !");
  print("---");
}

print("🎉 Tous les ennemis ont été vaincus !");`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="projet-final"
        title="Projet final : Combat entre héros et ennemis"
        showConsole={true}
        height="900px"
        initialCode=""
        autoFocus={true}
        allowCopyPaste={true}
        editorCols={8}
      />

      <Separator type="text" text="Prochaines étapes" color="gray-darker" size="md" />
      <SectionTitle>🧠 Tu as maintenant une vraie base de programmeur !</SectionTitle>
      
      <p>
        Si certaines choses ne sont pas encore claires, tu peux revenir en arrière et refaire les exercices. 
        Et surtout, n'aie pas peur de tester !
      </p>

      <Note type="info" title="💡 Conseils pour la suite :">
        <ul className="list-disc list-inside space-y-2">
          <li>Pratique régulièrement ce que tu as appris</li>
          <li>N'hésite pas à expérimenter avec le code</li>
          <li>Les erreurs sont normales, elles t'aident à apprendre</li>
          <li>Commence petit et améliore progressivement tes programmes</li>
        </ul>
      </Note>

      
      <Note type="" title="🎮 Prochaine aventure :">
        <p>Le prochain cours t'apprendra à créer ton premier mini-jeu vidéo !</p>
       
      </Note>
      <br/>
      <p className="text-center text-lg font-bold text-green-600 dark:text-green-400 mt-8">
        🎉 Félicitations ! Tu as maintenant une vraie base de programmeur ! 🎉
      </p>
      
      <BoldTextParagraph className="text-center">
        À très vite pour la suite de l'aventure ! 🚀
      </BoldTextParagraph>

  </>
  );
};

export default Chapitre9Content; 